import React, { useState, useEffect } from "react";
import {
  InfoIcon,
  CheckCircleIcon,
  AlertTriangleIcon,
  XCircleIcon,
  XIcon,
} from "../icons";

/**
 * Enhanced Alert component for displaying messages with improved animations and accessibility
 *
 * @param {Object} props
 * @param {string} props.type - Alert type (success, info, warning, error)
 * @param {React.ReactNode} props.children - Alert content
 * @param {string} props.className - Additional classes
 * @param {boolean} props.dismissible - Whether the alert can be dismissed
 * @param {Function} props.onDismiss - Function to call when alert is dismissed
 * @param {string} props.title - Optional title for the alert
 * @param {boolean} props.animate - Whether to animate the alert's entrance
 * @param {boolean} props.elevated - Whether to add shadow to the alert
 * @param {number} props.autoDismiss - Auto dismiss after X milliseconds (if provided)
 */
const Alert = ({
  type = "info",
  variant,
  children,
  className = "",
  dismissible = false,
  onDismiss,
  title,
  animate = true,
  elevated = false,
  autoDismiss = 0,
  ...props
}) => {
  const alertType = variant || type;
  const [visible, setVisible] = useState(true);
  const [exiting, setExiting] = useState(false);

  // Handle auto dismiss
  useEffect(() => {
    if (autoDismiss > 0 && dismissible) {
      const timer = setTimeout(() => {
        handleDismiss();
      }, autoDismiss);

      return () => clearTimeout(timer);
    }
  }, [autoDismiss, dismissible]);

  // Enhanced dismiss handler with exit animation
  const handleDismiss = () => {
    setExiting(true);
    // Wait for animation to complete before removing
    setTimeout(() => {
      setVisible(false);
      if (onDismiss) onDismiss();
    }, 300);
  };

  // If not visible, don't render
  if (!visible) return null;

  // Signal tones: the colour lives in the icon and a faint border only.
  const TONES = {
    success: "success",
    info: "info",
    warning: "warning",
    error: "danger",
    danger: "danger",
  };
  const tone = TONES[alertType] || "info";

  // Icon based on type
  const AlertIcon = () => {
    switch (tone) {
      case "success":
        return <CheckCircleIcon />;
      case "warning":
        return <AlertTriangleIcon />;
      case "danger":
        return <XCircleIcon />;
      default:
        return <InfoIcon />;
    }
  };

  const motionClass = animate ? (exiting ? "opacity-0" : "sg-pop") : "";

  return (
    <div
      className={`sg-alert transition-opacity ${motionClass} ${className}`}
      data-tone={tone}
      data-floating={elevated ? "true" : undefined}
      role="alert"
      aria-live={tone === "danger" ? "assertive" : "polite"}
      {...props}
    >
      <span className="sg-alert-icon" aria-hidden="true">
        <AlertIcon />
      </span>
      <div className="sg-alert-body">
        {title && <p className="sg-alert-title">{title}</p>}
        <div>{children}</div>
      </div>
      {dismissible && (
        <button
          type="button"
          className="sg-alert-close"
          onClick={handleDismiss}
          aria-label="Close"
        >
          <XIcon className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
};

/**
 * AlertGroup component for displaying multiple alerts with proper spacing
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Alert components
 * @param {string} props.position - Position of the alerts (top-right, top-left, bottom-right, bottom-left, top-center, bottom-center)
 * @param {string} props.className - Additional class names
 * @param {number} props.spacing - Spacing between alerts in pixels
 */
export const AlertGroup = ({
  children,
  position = "bottom-right",
  className = "",
  spacing = 3,
  ...props
}) => {
  // Position classes
  const positionClasses = {
    "top-right": "fixed top-4 right-4",
    "top-left": "fixed top-4 left-4",
    "bottom-right": "fixed bottom-4 right-4",
    "bottom-left": "fixed bottom-4 left-4",
    "top-center": "fixed top-4 left-1/2 transform -translate-x-1/2",
    "bottom-center": "fixed bottom-4 left-1/2 transform -translate-x-1/2",
  };

  return (
    <div
      className={`z-50 flex flex-col ${positionClasses[position]} ${className}`}
      style={{ gap: `${spacing * 0.25}rem` }}
      {...props}
    >
      {children}
    </div>
  );
};

export default Alert;
